/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { ClientProfile, ClientStatus, User } from "api/types";
import ReusableForm from "components/Form";
import { FieldType, FormData } from "components/types";
import { UseFormGetValues, UseFormSetValue, useForm } from "react-hook-form";
import {
  ABORIGIINAL_OR_TSI_OPTIONS,
  countriesOptions,
  enumToObjects,
  GENDER_OPTIONS,
  getTodayDate,
  languageOptions,
  MARITAL_STATUS_OPTIONS,
  RELIGION_OPTIONS,
  TITLE_OPTIONS,
} from "utils/util";
import { Option } from "components/Select";

export interface AdminUpdateProps {
  user: User;
  onSubmitFn: (data: FormData) => Promise<void>;
  isSuperAdmin?: boolean;
  refreshData?: () => void;
}

const validateMedicareNumber = (value: string) => {
  const codeLength = value.length;
  return codeLength >= 10 && codeLength <= 11;
};

const validateClientRecipientId = (value: string) => {
  const codeLength = value.length;
  return codeLength === 10;
};

const validateCentrelinkNumber = (value: string) => {
  const codeLength = value.length; 
  if (codeLength === 10) {
    const valueToUpper = value.toUpperCase();
    const first9Digits = valueToUpper.slice(0, 9);
    const lastDigits = valueToUpper.slice(-1);
    return /^\d+$/.test(first9Digits) && /^[a-zA-Z]$/.test(lastDigits);
  } else {
    return false;
  }
};

const validateAgedCareId = (value: string) => {
  const codeLength = value.length; 
  if (codeLength === 10) {
    const valueToUpper = value.toUpperCase();
    const first2Chars = valueToUpper.slice(0, 2);
    const last8Digits = valueToUpper.slice(2);

    return first2Chars === "AC" && /^\d{8}$/.test(last8Digits);
  } else {
    return false;
  }
};

const yesNoConvert = (data: Boolean | null) => {
  if (data === null) {
    return "";
  } else {
    return data ? "Yes" : "No";
  }
}

const getAge = (dateOfBirth: string | Date | null | undefined): number | undefined => {
  if (!dateOfBirth) return undefined;
  
  const dob = typeof dateOfBirth === 'string' 
    ? new Date(dateOfBirth) 
    : dateOfBirth;
    
  if (isNaN(dob.getTime())) {
    return undefined;
  }

  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  const dayDiff = today.getDate() - dob.getDate();

  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
    age--;
  }

  return age > 0 ? age : undefined;
};

const statusOptions = enumToObjects(ClientStatus).map((option) => ({
  label: option.label.split("_").join(" "),
  value: option.value,
}));

export const getPersonalDetailsFields = (
  getValue: UseFormGetValues<FormData>,
  setValue: UseFormSetValue<FormData>,
  clientProfile: ClientProfile,
  user: User,
  setResidentialState: (value: string | undefined) => void,
  residentialState: string | undefined,
  watchedFields: FormData,
  titleOptions: Option[],
  onChangeTitle: (title: {label: String, value: String}) => void
) => [
  {
    name: "gender",
    label: "Gender *",
    type: FieldType.Radio,
    options: GENDER_OPTIONS,
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    defaultValue: clientProfile.gender,
  },
  {
    name: "title",
    label: "Title *",
    type: FieldType.Select,
    placeholder: "Select",
    options: titleOptions,
    onChange: onChangeTitle,
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    defaultValue: user.title,
    hideSearch: true,
  },
  {
    name: "firstName",
    label: "First Name *",
    type: FieldType.Input,
    placeholder: "John",
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    defaultValue: user.firstName,
  },
  {
    name: "lastName",
    label: "Last Name *",
    type: FieldType.Input,
    placeholder: "Doe",
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    defaultValue: user.lastName,
  },
  {
    name: "preferredName",
    label: "Preferred Name *",
    type: FieldType.Input,
    placeholder: "Johnny",
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    defaultValue: clientProfile.preferredName,
  },
  {
    name: "dob",
    label: "Date of Birth *",
    type: FieldType.Calendar,
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    wrapperClassName: "w-full",
    className: "w-full",
    defaultValue: clientProfile.dob,
    maxDate: getTodayDate(),
  },
  {
    name: "age",
    label: "Age *",
    type: FieldType.Input,
    placeholder: "",
    parentFormContainerClassName:
    "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    isDisabled: true,
    defaultValue: getAge(clientProfile.dob),
  },
  {
    name: "birthCountry",
    label: "Country of Birth *",
    type: FieldType.Select,
    options: countriesOptions,
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    defaultValue: clientProfile.birthCountry,
  },
  {
    name: "homeRegion",
    label: "Home region",
    type: FieldType.Input,
    placeholder: "John",
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    defaultValue: clientProfile.homeRegion,
    className: "w-full",
  },
  {
    name: "preferredLanguages",
    label: "Preferred language *",
    type: FieldType.MultiSelect,
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    options: languageOptions,
    defaultValue: clientProfile.preferredLanguages,
  },
  {
    name: "maritalStatus",
    label: "Marital status *",
    type: FieldType.Select,
    placeholder: "Select",
    options: MARITAL_STATUS_OPTIONS,
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    hideSearch: true,
    defaultValue: clientProfile.maritalStatus,
  },
  ...(watchedFields?.maritalStatus === "Married"
    ? [
        {
          name: "spouseWithEicare",
          label: "Has spouse with EICARE",
          type: FieldType.Radio,
          options: [
            { label: "Yes", value: "Yes" },
            { label: "No", value: "No" },
          ],
          parentFormContainerClassName:
            "md:w-1/3 w-full inline-block align-top md:pr-6",
          containerClassName: "w-full",
          className: "w-full",
          defaultValue: yesNoConvert(clientProfile.spouseWithEicare),
        },
      ]
    : []
  ),
  ...(watchedFields?.maritalStatus === "Married" &&  watchedFields?.spouseWithEicare === "Yes"
    ? [
        {
          name: "spouseName",
          label: "Spouse name",
          type: FieldType.Select,
          placeholder: "Select",
          options: [
            { label: "Sarah Lee", value: "Sarah Lee" },
            { label: "Test 1", value: "Test 1" },
            { label: "Test 2", value: "Test 2" },
            { label: "Test 3", value: "Test 3" },
            { label: "Test 4", value: "Test 4" },
            { label: "Test 5", value: "Test 5" },
          ],
          parentFormContainerClassName:
            "md:w-1/3 w-full inline-block align-top md:pr-6",
          containerClassName: "w-full",
          className: "w-full",
          defaultValue: clientProfile.spouseName,
        }
      ]
    : []
  ),
  {
    name: "interpreterRequired",
    label: "Interpreter required",
    type: FieldType.Radio,
    options: [
      { label: "Yes", value: "Yes" },
      { label: "No", value: "No" },
    ],
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    defaultValue: yesNoConvert(clientProfile.interpreterRequired),
  },
  {
    name: "religion",
    label: "Religion",
    type: FieldType.Select,
    placeholder: "Select",
    options: RELIGION_OPTIONS,
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    defaultValue: clientProfile.religion,
    hideSearch: true,
  },
  {
    name: "landlord",
    label: "Landlord",
    type: FieldType.Radio,
    options: [
      { label: "Yes", value: "Yes" },
      { label: "No", value: "No" },
    ],
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    defaultValue: yesNoConvert(clientProfile.landlord),
  },
  {
    name: "keylockNumber",
    label: "Keylock number",
    type: FieldType.Input,
    placeholder: "C12345",
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    defaultValue: clientProfile.keylockNumber,
    onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
      e.currentTarget.value = e.currentTarget.value.toUpperCase();
    },
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === " ") {
        e.preventDefault();
      }
    },
  },
  {
    name: "notAtHomeNote",
    label: "Not at home note",
    type: FieldType.Textarea,
    placeholder: "The key is under the flower pot",
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    defaultValue: clientProfile.notAtHomeNote,
  },
  {
    name: "haveACar",
    label: "Have a car",
    type: FieldType.Radio,
    options: [
      { label: "Yes", value: "Yes" },
      { label: "No", value: "No" },
    ],
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    defaultValue: yesNoConvert(clientProfile.haveACar),
  },
  {
    name: "driving",
    label: "Driving",
    type: FieldType.Radio,
    options: [
      { label: "Yes", value: "Yes" },
      { label: "No", value: "No" },
    ],
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    defaultValue: yesNoConvert(clientProfile.driving),
  },
  {
    name: "disabledParkingSticker",
    label: "Have disability parking sticker",
    type: FieldType.Radio,
    options: [
      { label: "Yes", value: "Yes" },
      { label: "No", value: "No" },
    ],
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    defaultValue: yesNoConvert(clientProfile.disabledParkingSticker),
  },
  {
    name: "haveTaxiCard",
    label: "Have taxi card",
    type: FieldType.Radio,
    options: [
      { label: "Yes", value: "Yes" },
      { label: "No", value: "No" },
    ],
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    defaultValue: yesNoConvert(clientProfile.haveTaxiCard),
  },
  ...(watchedFields?.haveTaxiCard === "No"
    ? [
        {
          name: "needTaxiCardFromEicare",
          label: "Require taxi card from EICARE",
          type: FieldType.Radio,
          options: [
            { label: "Yes", value: "Yes" },
            { label: "No", value: "No" },
          ],
          parentFormContainerClassName:
            "md:w-1/3 w-full inline-block align-top md:pr-6",
          containerClassName: "w-full",
          className: "w-full",
        }
      ]
    : []
  ),
  ...(watchedFields?.haveTaxiCard === "No" &&  watchedFields?.needTaxiCardFromEicare === "Yes"
    ? [
        {
          name: "taxiCardReceived",
          label: "Received taxi card",
          type: FieldType.Radio,
          options: [
            { label: "Yes", value: "Yes" },
            { label: "No", value: "No" },
          ],
          parentFormContainerClassName:
            "md:w-1/3 w-full inline-block align-top md:pr-6",
          containerClassName: "w-full",
          className: "w-full",
        }
      ]
    : []
  ),
  {
    name: "agedCareId",
    label: "Aged care id *",
    type: FieldType.Input,
    placeholder: "AC12345678",
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === " ") {
        e.preventDefault();
      }
    },
    onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
      e.currentTarget.value = e.currentTarget.value.toUpperCase();
    },
    defaultValue: clientProfile.agedCareId,
    validationFunction: validateAgedCareId,
    validationErrorMessage: "Invalid aged care id format",
    requiredCondition: watchedFields && watchedFields.agedCareId,
  },
  {
    name: "medicareCardNo",
    label: "Medicare number *",
    type: FieldType.Input,
    placeholder: "1234567890",
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    subType: "number",
    defaultValue: clientProfile.medicareCardNo,
    validationFunction: validateMedicareNumber,
    validationErrorMessage: "Invalid medicare number format",
    requiredCondition: watchedFields && watchedFields.medicareCardNo,
  },
  {
    name: "medicareExpiryDate",
    label: "Medicare expiry *",
    type: FieldType.Calendar,
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    wrapperClassName: "w-full",
    className: "w-full",
    minDate: getTodayDate(),
    defaultValue: clientProfile.medicareExpiryDate,
  },
  {
    name: "clientRecipientId",
    label: "Client recipient id *",
    type: FieldType.Input,
    placeholder: "1234567890",
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    subType: "number",
    defaultValue: clientProfile.clientRecipientId,
    validationFunction: validateClientRecipientId,
    validationErrorMessage: "Invalid client recipient id format",
    requiredCondition: watchedFields && watchedFields.clientRecipientId,
  },
  {
    name: "centrelinkNo",
    label: "Centrelink number *",
    type: FieldType.Input,
    placeholder: "123456789A",
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === " ") {
        e.preventDefault();
      }
    },
    onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
      e.currentTarget.value = e.currentTarget.value.toUpperCase();
    },
    defaultValue: clientProfile.centrelinkNo,
    validationFunction: validateCentrelinkNumber,
    validationErrorMessage: "Invalid centrelink number format",
    requiredCondition: watchedFields && watchedFields.centrelinkNo,
  },
  {
    name: "aboriginalTSIStatus",
    label: "Aboriginal or TSI",
    type: FieldType.Select,
    placeholder: "Select",
    options: ABORIGIINAL_OR_TSI_OPTIONS,
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    hideSearch: true,
    defaultValue: clientProfile.aboriginalTSIStatus,
  },
  {
    name: "profileStatus",
    label: "Profile status *",
    type: FieldType.Select,
    placeholder: "Select",
    options: clientProfile?.profileStatus !== ClientStatus.Onboarding ? statusOptions : [
      { label: "Onboarding", value: "Onboarding" }
    ],
    parentFormContainerClassName:
      "md:w-1/3 w-full inline-block align-top md:pr-6",
    containerClassName: "w-full",
    className: "w-full",
    defaultValue: clientProfile.profileStatus,
    hideSearch: true,
  },
];

export default function AdminUpdatePersonalDetails({
  user,
  onSubmitFn,
  isSuperAdmin,
}: AdminUpdateProps) {
  const { formState, getValues, setValue, watch, ...formMethods } =
    useForm<FormData>();
  const { clientProfile } = user;
  const [residentialState, setResidentialState] = useState<string | undefined>(
    undefined
  );

  const watchedFields = watch();
  const selectedGender = watchedFields.gender;
  const selectedTitle = watchedFields.title;

  useEffect(() => {
    if (clientProfile.haveTaxiCard === false) {
      setValue("needTaxiCardFromEicare", yesNoConvert(clientProfile.needTaxiCardFromEicare));
      if (clientProfile.needTaxiCardFromEicare === true) {
        setValue("taxiCardReceived", yesNoConvert(clientProfile.taxiCardReceived));
      }
    }
  }, []);

  useEffect(() => {
    if (watchedFields.maritalStatus !== "Married") {
      setValue("spouseWithEicare", null);
      setValue("spouseName", null);
    }

    if (watchedFields.spouseWithEicare === "No") {
      setValue("spouseName", null);
    }

    if (watchedFields.haveTaxiCard === "Yes") {
      setValue("needTaxiCardFromEicare", null);
      setValue("taxiCardReceived", null);
    }

    if (watchedFields.needTaxiCardFromEicare === "No") {
      setValue("taxiCardReceived", null);
    }
  }, [watchedFields.maritalStatus, watchedFields.spouseWithEicare, watchedFields.haveTaxiCard, watchedFields.needTaxiCardFromEicare]);
  
  useEffect(() => {
    if (watchedFields.dob) {
      setValue("age", getAge(watchedFields.dob));
    }
  }, [watchedFields.dob]);

  const onSubmit = async (data: FormData) => onSubmitFn(data);

  /* ---------- FILTER TITLE BASED ON GENDER ---------- */
  const filteredTitleOptions = React.useMemo(() => {
    if (selectedGender === "Male") {
      return TITLE_OPTIONS.filter(o =>
        [ "Dr", "Mr"].includes(o.value)
      );
    }

    if (selectedGender === "Female") {
      return TITLE_OPTIONS.filter(o =>
        ["Dr", "Mrs", "Miss"].includes(o.value)
      );
    }

    return TITLE_OPTIONS;
  }, [selectedGender]);

  /* ---------- RESET INVALID VALUES ---------- */
  useEffect(() => {
    if (
      selectedTitle &&
      !filteredTitleOptions.some(o => o.value === selectedTitle)
    ) {
      setValue("title", "");
    }
  }, [filteredTitleOptions, selectedTitle, setValue]);

  const onChangeTitle = (title: {label: String, value: String}) => {
    if (title.value === "Mr") {
      setValue("gender", "Male");
    } else if (title.value === "Mrs" || title.value === "Miss") {
      setValue("gender", "Female");
    }
  }
  
  return (
    <div className="bg-white rounded-md">
      <div className="pl-6 pr-0 pb-0">
        <ReusableForm
          fields={
            getPersonalDetailsFields(
              getValues,
              setValue,
              clientProfile,
              user,
              setResidentialState,
              residentialState,
              watchedFields,
              filteredTitleOptions,
              onChangeTitle
            )
          }
          onSubmit={onSubmit}
          id="edit-PERSONAL_DETAILS"
          formClassName="pt-1 pb-6"
          formMethods={{
            ...formMethods,
            formState,
            getValues,
            setValue,
            watch,
          }}
        />
      </div>
    </div>
  );
}
